import { useCallback, useEffect, useState } from 'react';
import OrganizationalChart from './OrganizationalChart';

interface Props {
  activeIndex: string;
  handleOnClick: (index: string) => void;
  detailData: any;
}

const OwnershipStructure = ({ activeIndex, handleOnClick, detailData }: Props) => {
  const [sliderValue, setSliderValue] = useState<number>(15);
  const [filteredGraphData, setFilteredGraphData] = useState<any | null>(null);

  const ownershipWarnings = detailData.companyProfile?.companyProfile?.ownershipStructure?.warnings ?? [];

  const filterGraphData = useCallback(() => {
    const currentData = JSON.parse(JSON.stringify(detailData.companyProfile?.companyProfile?.ownershipStructure ?? []));

    const removedNodes: any = [];
    const rootConnectedNodes: any = [];
    const rootNode = currentData.name;

    // Get all connected nodes to the root node
    // This is used for the hidden nodes percentage
    currentData.graph.forEach((link: any) => {
      if (link.source === rootNode) {
        rootConnectedNodes.push(link.target);
      }
    });

    // Step 1: Filter nodes based on the slider value
    let filteredNodes = currentData.owners.filter((node: any) => {
      const nodeShare = node.share * 100;

      // The usual case where the node is not the root node
      // or the value is above the slider value
      if (rootNode === node.name || node.share === 'unknown' || nodeShare >= sliderValue) {
        return true;
      // The case in which companies do not have total ownership (==0)
      // and we have to take the direct ownership percent from their link
      } else if (nodeShare <= 0) {
        const filteredLink = currentData.graph.find((link: any) => node.name === link.target);
        if (filteredLink.ownership >= sliderValue) {
          return true;
        }
      }

      if (rootConnectedNodes.includes(node.name)) {
        removedNodes.push(node);
      }

      return false;
    });

    // Step 2: Filter links based on the filtered nodes
    let filteredLinks = currentData.graph.filter((link: any) => {
      return filteredNodes.find((node: any) => node.name === link.source)
        && filteredNodes.find((node: any) => node.name === link.target);
    });

    // Step 3: Ensure all nodes are connected through their links
    const connectedNodes = new Set<string>();
    const traverse = (nodeName: string) => {
      if (connectedNodes.has(nodeName)) return;
      connectedNodes.add(nodeName);
      filteredLinks.forEach((link: any) => {
        if (link.source === nodeName) traverse(link.target);
        if (link.target === nodeName) traverse(link.source);
      });
    };
    traverse(rootNode);

    filteredNodes = filteredNodes.filter((node: any) => connectedNodes.has(node.name));
    filteredLinks = filteredLinks.filter((link: any) => connectedNodes.has(link.source) && connectedNodes.has(link.target));

    // Step 4: Create a new node for hidden nodes
    if (removedNodes.length) {
      const totalNodes = currentData.owners.length - filteredNodes.length;
      const totalFirstLevelPercentage = removedNodes.reduce((accumulator: any, currentValue: any) => {
        // Always take the values from the links to the root node, since they are the only logical ones to display when hidden
        const filteredLink = currentData.graph.find((link: any) => (currentValue.name === link.target && rootNode === link.source));
        return accumulator + (filteredLink ? filteredLink.ownership / 100 : 0);
      }, 0);

      const newNode = { name: `${totalNodes} Shareholders`, type: 'hidden', is_ubo: false, share: totalFirstLevelPercentage, level: 1 };
      const newLink = { source: rootNode, target: `${totalNodes} Shareholders`, ownership: totalFirstLevelPercentage * 100 };

      filteredNodes.push(newNode);
      filteredLinks.push(newLink);
    }

    const data = {
      rootNode: rootNode,
      owners: filteredNodes,
      graph: filteredLinks
    };

    setFilteredGraphData(data);
  }, [detailData.companyProfile?.companyProfile?.ownershipStructure, sliderValue]);

  // The following issues should be handled when we move to a slider component from a library:
  // - it feels highly buggy and annoying to use
  // - if you pause dragging, it will trigger the value
  // Custom debounce function
  // Due to React not using (on)input and (on)change like in JS
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      filterGraphData();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [sliderValue, filterGraphData]);

  return (
    <div className={`card accordion ${activeIndex === 'ownershipStructure' ? 'active' : ''}`}>
      <div className="accordion-heading" onClick={() => handleOnClick('ownershipStructure')}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-4 align-items-center">
            <h4 className="mb-0">OWNERSHIP STRUCTURE</h4>
          </div>
          <div>
            <img
              className="chevron-icon"
              src={require('src/assets/images/icons/chevron-down.svg').default}
              alt="chevron-down icon"
            />
          </div>
        </div>
      </div>

      {/*
        The -invisible accordion content is used to load the graph in a hidden way 
        which allows it to be calculated and sent to the PDF report without any additional steps
      */}
      <div className="accordion-content accordion-content-invisible">
        <div className="ownership-filter-and-warnings-container">
          {/* Filter component - slider */}
          <div className="ownership-filter-container">
            <div>Total Ownership &ge; <b>{sliderValue}%</b></div>
            <div className="ownership-filter-range">
              <label className="ownership-left-label" htmlFor="filterRange">0%</label>
              <input className="ownership-input" type="range" id="filterRange" name="filterRange" min="0" max="100" step="5" value={sliderValue} autoComplete="off" onChange={(e) => setSliderValue(Number(e.target.value))} />
              <label className="ownership-right-label" htmlFor="filterRange">100%</label>
            </div>
          </div>

          {
            ownershipWarnings.length
            ? (
              <div className="ownership-warnings-container">
                <p>Warnings:</p>
                {
                  ownershipWarnings.map((warning: any) => <p className="ownership-warning">&#9888; {warning}</p>)
                }
              </div>
            ) : null
          }
        </div>

        {
          filteredGraphData
            ? <OrganizationalChart data={filteredGraphData} />
            : null
        }
      </div>
    </div>
  );
};

export default OwnershipStructure;
